import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { deleteInvoice, getInvoice, listInvoices, saveInvoice, sendInvoiceEmail } from "@/lib/admin-extra.functions";
import { downloadInvoicePdf } from "@/lib/invoice-pdf";
import type { InvoiceForDoc } from "@/lib/invoice-details";
import { FileDown, Mail } from "lucide-react";
import { DataTable, type DataTableColumn } from "@/components/admin/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/invoices")({
  component: InvoicesPage,
});

type Invoice = {
  id: string;
  invoice_number: string;
  client_name: string;
  email: string;
  phone: string | null;
  address: string | null;
  status: string;
  issue_date: string;
  due_date: string | null;
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  notes: string | null;
  created_at: string;
};

type Item = { description: string; qty: number; unit_price: number };

const money = (n: number) => "R" + Number(n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);

function InvoicesPage() {
  const qc = useQueryClient();
  const list = useServerFn(listInvoices);
  const save = useServerFn(saveInvoice);
  const remove = useServerFn(deleteInvoice);
  const fetchOne = useServerFn(getInvoice);
  const sendEmail = useServerFn(sendInvoiceEmail);
  const [open, setOpen] = useState(false);

  const { data, isPending } = useQuery({ queryKey: ["admin", "invoices"], queryFn: () => list() });
  const rows = (data ?? []) as Invoice[];

  const [form, setForm] = useState({
    client_name: "",
    email: "",
    phone: "",
    address: "",
    status: "draft",
    issue_date: today(),
    due_date: "",
    tax: 0,
    discount: 0,
    notes: "",
  });
  const [items, setItems] = useState<Item[]>([{ description: "", qty: 1, unit_price: 0 }]);

  const subtotal = items.reduce((s, i) => s + i.qty * i.unit_price, 0);
  const total = Math.max(0, subtotal + Number(form.tax || 0) - Number(form.discount || 0));

  const saveMut = useMutation({
    mutationFn: () =>
      save({
        data: {
          client_name: form.client_name,
          email: form.email,
          phone: form.phone || null,
          address: form.address || null,
          status: form.status as "draft" | "sent" | "paid" | "void",
          issue_date: form.issue_date,
          due_date: form.due_date || null,
          tax: Number(form.tax || 0),
          discount: Number(form.discount || 0),
          notes: form.notes || null,
          items: items.filter((i) => i.description.trim()),
        },
      }),
    onSuccess: () => {
      toast.success("Invoice saved");
      setOpen(false);
      setItems([{ description: "", qty: 1, unit_price: 0 }]);
      setForm((f) => ({ ...f, client_name: "", email: "", phone: "", address: "", notes: "" }));
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const sendMut = useMutation({
    mutationFn: (id: string) => sendEmail({ data: { id } }),
    onSuccess: () => {
      toast.success("Invoice emailed to client");
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (ids: string[]) => remove({ data: { ids } }),
    onSuccess: () => {
      toast.success("Invoice deleted");
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const columns: DataTableColumn<Invoice>[] = [
    { id: "invoice_number", header: "Invoice", value: (r) => r.invoice_number, sortable: true },
    { id: "client_name", header: "Client", value: (r) => r.client_name, sortable: true },
    { id: "email", header: "Email", value: (r) => r.email },
    { id: "issue_date", header: "Issued", value: (r) => r.issue_date, sortable: true },
    { id: "status", header: "Status", value: (r) => r.status, sortable: true },
    { id: "total", header: "Total", value: (r) => Number(r.total), cell: (r) => money(r.total), sortable: true },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Create and track invoices for treatments and shop orders.</p>
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New invoice
        </Button>
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        loading={isPending}
        searchPlaceholder="Search invoices"
        exportFileName="invoices"
        emptyMessage="No invoices yet."
        filters={[]}
        rowActions={(r) => (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              title="Download PDF"
              onClick={async () => {
                try {
                  const full = (await fetchOne({ data: { id: r.id } })) as unknown as InvoiceForDoc;
                  await downloadInvoicePdf(full);
                } catch (e) {
                  toast.error((e as Error).message);
                }
              }}
            >
              <FileDown className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline">PDF</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              title="Email to client"
              disabled={sendMut.isPending}
              onClick={() => {
                if (confirm(`Email ${r.invoice_number} to ${r.email}?`)) sendMut.mutate(r.id);
              }}
            >
              <Mail className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline">Email</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm(`Delete ${r.invoice_number}?`)) deleteMut.mutate([r.id]);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
        bulkActions={(ids, clear) => (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              deleteMut.mutate(ids);
              clear();
            }}
          >
            Delete {ids.length}
          </Button>
        )}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New invoice</DialogTitle>
            <DialogDescription>Line items are totalled automatically.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Client name">
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["draft", "sent", "paid", "void"].map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Issue date">
              <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
            </Field>
            <Field label="Due date">
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Address">
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </Field>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Line items</p>
            {items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2">
                <Input
                  className="col-span-6"
                  placeholder="Description"
                  value={it.description}
                  onChange={(e) =>
                    setItems(items.map((x, i) => (i === idx ? { ...x, description: e.target.value } : x)))
                  }
                />
                <Input
                  className="col-span-2"
                  type="number"
                  min={0}
                  value={it.qty}
                  onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, qty: Number(e.target.value) } : x)))}
                />
                <Input
                  className="col-span-3"
                  type="number"
                  min={0}
                  step="0.01"
                  value={it.unit_price}
                  onChange={(e) =>
                    setItems(items.map((x, i) => (i === idx ? { ...x, unit_price: Number(e.target.value) } : x)))
                  }
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="col-span-1"
                  onClick={() => setItems(items.length > 1 ? items.filter((_, i) => i !== idx) : items)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setItems([...items, { description: "", qty: 1, unit_price: 0 }])}>
              <Plus className="mr-2 h-4 w-4" />
              Add item
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tax">
              <Input type="number" min={0} step="0.01" value={form.tax} onChange={(e) => setForm({ ...form, tax: Number(e.target.value) })} />
            </Field>
            <Field label="Discount">
              <Input
                type="number"
                min={0}
                step="0.01"
                value={form.discount}
                onChange={(e) => setForm({ ...form, discount: Number(e.target.value) })}
              />
            </Field>
          </div>

          <Field label="Notes">
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </Field>

          <div className="rounded-lg border p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{money(subtotal)}</span>
            </div>
            <div className="mt-1 flex justify-between font-semibold">
              <span>Total</span>
              <span>{money(total)}</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              {saveMut.isPending ? "Saving..." : "Save invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
